/**
 * Settings — what exists so far, stated plainly, plus the theme toggle.
 *
 * Most of this screen is a list of things the system does not do yet. That is
 * more useful than a page of switches that don't connect to anything.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { titleCase } from "../lib/format";
import { Card, Chip, PageHeader } from "../components/ui";
import { SyncSummary } from "../components/SyncStatus";
import { BusinessDetails, XeroSettings } from "../components/BusinessSettings";
import { ChannelSettings } from "../components/ChannelSettings";
import { DocumentSettings } from "../components/DocumentSettings";
import { ChangeOwnPassword, PeopleSettings } from "../components/PeopleSettings";
import { device } from "../lib/device";
import { VERSION, outOfStep } from "../version";

type Theme = "system" | "light" | "dark";
const THEME_KEY = "keystone.theme";

function readTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    /* ignore */
  }
  return "system";
}

export default function Settings() {
  const { session, signOut, can } = useAuth();
  const canEdit = can("owner", "office");
  const [theme, setTheme] = useState<Theme>(readTheme);

  const health = useQuery({ queryKey: ["health"], queryFn: () => api.health(), retry: false });
  const orgName = useQuery({ queryKey: ["org"], queryFn: () => api.org.get(), staleTime: 10_000, retry: false });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* a lost preference is not worth an error */
    }
  }, [theme]);

  return (
    <div className="page">
      <PageHeader title="Settings" />

      <Card title="You">
        <dl className="kv">
          <dt>Signed in as</dt>
          <dd>{session?.email}</dd>
          <dt>Role</dt>
          <dd>{titleCase(session?.role ?? "")}</dd>
          <dt>Business</dt>
          <dd>{orgName.data?.name ?? "—"}</dd>
        </dl>
        <div className="btn-row" style={{ marginTop: 12 }}>
          <button className="btn" type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
        <ChangeOwnPassword />
      </Card>

      {can("owner", "office", "supervisor") && <PeopleSettings isOwner={can("owner")} />}

      <BusinessDetails canEdit={canEdit} />
      {canEdit && <ChannelSettings />}
      <DocumentSettings canEdit={canEdit} />
      <XeroSettings canEdit={canEdit} />

      <Card title="Appearance">
        <div className="segmented">
          {(["system", "light", "dark"] as Theme[]).map((option) => (
            <button
              key={option}
              type="button"
              className={theme === option ? "active" : ""}
              aria-pressed={theme === option}
              onClick={() => setTheme(option)}
            >
              {titleCase(option)}
            </button>
          ))}
        </div>
        <p className="tiny muted" style={{ marginTop: 10 }}>
          System follows your phone or desktop. Dark is the one you want in a comms
          room at night.
        </p>
      </Card>

      <Card title="Server">
        <dl className="kv">
          <dt>API</dt>
          <dd>
            {health.isPending ? (
              "checking…"
            ) : health.error ? (
              <Chip tone="crit" dot>
                unreachable
              </Chip>
            ) : (
              <Chip tone="ok" dot>
                {health.data.status} · {health.data.env}
              </Chip>
            )}
          </dd>
          <dt>Version</dt>
          <dd className="mono">
            Keystone {VERSION}
            {health.data?.version && outOfStep(health.data.version) && (
              <>
                {" "}
                <Chip tone="warn">server is {health.data.version} — reload to update</Chip>
              </>
            )}
          </dd>
        </dl>
      </Card>

      <Card title="This device">
        <SyncSummary />
        <dl className="kv" style={{ marginTop: 14 }}>
          <dt>Device code</dt>
          <dd>
            {/* What a customer reads off a provisional number. Worth showing,
                because "J-D4-0007" only makes sense to someone who knows this
                iPad is D4. */}
            <code>{device().shortCode}</code>
          </dd>
        </dl>
      </Card>

      <Card title="Not built yet">
        <p className="tiny" style={{ marginBottom: 12, lineHeight: 1.7 }}>
          Rather than show switches that do nothing, here is what this screen will
          hold once the cards behind it land.
        </p>
        <ul className="plainlist">
          <li>
            <b className="mono">CORE-3</b> Devices, refresh tokens, sign out everywhere
          </li>
          <li>
            <b className="mono">COMPLY-4</b> Licence, insurance and induction expiry
          </li>
          <li>
            <b className="mono">API-2</b> Webhooks and API tokens — included, not an upsell
          </li>
          <li>
            <b className="mono">STOCK-5</b> Supplier catalogue feeds
          </li>
        </ul>
      </Card>
    </div>
  );
}
